import React, { useState } from 'react';
import { useInventory } from '../context/InventoryContext';
import { formatDate } from '../utils/dateFormat';
import BulkUpload from '../components/BulkUpload';
import { useAuth } from '../context/AuthContext';
import { FaEdit, FaTrash, FaPlus, FaDownload, FaPlusCircle } from 'react-icons/fa';
import Pagination from '../components/Pagination';
import { toast } from 'react-toastify';

const DeviceInventory = () => {
  const {
    devices, addDevice, updateDevice, deleteDevice,
    companies, subCompanies, getSubCompaniesByCompany,
    getEntityDisplayName
  } = useInventory();

  const [form, setForm] = useState({
    imei: '',
    slNumber: '',
    model: '',
    make: '',
    purchaseDate: '',
    status: 'new stock',
    parentCompanyId: '',
    companyId: '',
    image: '' 
  });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [errors, setErrors] = useState({});
  const { currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'super_admin';

  // Store original values for comparison when editing
  const [originalFormValues, setOriginalFormValues] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [statusFilter, setStatusFilter] = useState('');
  const [makeFilter, setMakeFilter] = useState(''); // ✅ NEW: Make filter
  const [modelFilter, setModelFilter] = useState(''); // ✅ NEW: Model filter
  const [searchQuery, setSearchQuery] = useState('');

  const [showBulkAddMenu, setShowBulkAddMenu] = useState(false);
  const [showBulkUpdateMenu, setShowBulkUpdateMenu] = useState(false);

  // State for custom model
  const [showAddModelInput, setShowAddModelInput] = useState(false);
  const [newModel, setNewModel] = useState('');
  
  // Load saved models from localStorage or use default
  const [modelOptions, setModelOptions] = useState(() => {
    const savedModels = localStorage.getItem('deviceModels');
    if (savedModels) {
      try {
        return JSON.parse(savedModels);
      } catch (e) {
        return ['AIS 140', 'LC 100', 'ATL', 'teltonica', 'TS101_BASIC', 'ITS_ODB', 'BB', 'TM33', 'ATLANTA', 'S101', 'AIS140Converted'];
      }
    }
    return ['AIS 140', 'LC 100', 'ATL', 'teltonica', 'TS101_BASIC', 'ITS_ODB', 'BB', 'TM33', 'ATLANTA', 'S101', 'AIS140Converted'];
  });

  // Helper function to check if any field was actually changed
  const hasChanges = (original, current) => {
    if (!original) return false;
    // Compare all fields except id and image
    const fieldsToCompare = ['imei', 'slNumber', 'model', 'make', 'purchaseDate', 'status', 'parentCompanyId', 'companyId'];
    
    for (let field of fieldsToCompare) {
      const originalValue = (original[field] || '').trim();
      const currentValue = (current[field] || '').trim();
      if (originalValue !== currentValue) {
        return true;
      }
    }
    
    return false;
  };

  // Get unique statuses from devices for the filter dropdown
  const uniqueStatuses = React.useMemo(() => {
    const statusSet = new Set();
    devices.forEach(device => {
      if (device.status) {
        statusSet.add(device.status.toLowerCase().trim());
      }
    });
    return Array.from(statusSet).sort();
  }, [devices]);

  // ✅ NEW: Get unique makes from devices for the filter dropdown
  const uniqueMakes = React.useMemo(() => {
    const makeSet = new Set();
    devices.forEach(device => {
      if (device.make) {
        makeSet.add(device.make.trim());
      }
    });
    return Array.from(makeSet).sort();
  }, [devices]);

  // ✅ NEW: Get unique models from devices for the filter dropdown
  const uniqueModels = React.useMemo(() => {
    const modelSet = new Set();
    devices.forEach(device => {
      if (device.model) {
        modelSet.add(device.model.trim());
      }
    });
    return Array.from(modelSet).sort();
  }, [devices]);

  const filteredDevices = (isSuperAdmin
    ? devices
    : devices.filter(d => {
        if (!d.companyId) return true;
        return d.companyId === currentUser.companyId ||
          subCompanies.some(s => s.id === d.companyId && s.parentCompanyId === currentUser.companyId);
      })
  ).filter(d => {
    // Fix: Case-insensitive status filter with proper trimming
    if (statusFilter) {
      const deviceStatus = String(d.status || '').toLowerCase().trim();
      const filterStatus = String(statusFilter).toLowerCase().trim();
      if (deviceStatus !== filterStatus) return false;
    }
    
    // ✅ NEW: Make filter
    if (makeFilter) {
      const deviceMake = String(d.make || '').toLowerCase().trim();
      const filterMake = String(makeFilter).toLowerCase().trim();
      if (deviceMake !== filterMake) return false;
    }
    
    // ✅ NEW: Model filter
    if (modelFilter) {
      const deviceModel = String(d.model || '').toLowerCase().trim();
      const filterModel = String(modelFilter).toLowerCase().trim();
      if (deviceModel !== filterModel) return false;
    }
    
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    return d.imei.toLowerCase().includes(q) || 
           (d.slNumber || '').toLowerCase().includes(q) ||
           (d.model || '').toLowerCase().includes(q) ||
           (d.make || '').toLowerCase().includes(q);
  });

  const totalItems = filteredDevices.length;
  const paginatedDevices = filteredDevices.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // UTILITY FOR CSV DOWNLOAD
  const handleDownloadExcel = () => {
    const headers = [
      'IMEI', 'SL No', 'Model', 'Make', 'Purchase Date', 
      'Warranty End', 'Installation Date', 'Status', 'Company'
    ];

    const rows = filteredDevices.map(d => [
      d.imei,
      d.slNumber || '-',
      d.model || '-',
      d.make || '-',
      d.purchaseDate || '-',
      d.warrantyEnd || '-',
      d.installationDate || '-',
      d.status,
      getEntityDisplayName(d.companyId) || '-'
    ]);

    const csvContent = [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `Device_Inventory_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  React.useEffect(() => {
    setCurrentPage(1);
  }, [isSuperAdmin, currentUser?.companyId]);

  const resetForm = () => {
    setForm({
      imei: '', slNumber: '', model: '', make: '',
      purchaseDate: '', status: 'new stock',
      parentCompanyId: '', companyId: '',
      image: ''
    });
    setEditingId(null);
    setShowForm(false);
    setErrors({});
    setShowAddModelInput(false);
    setNewModel('');
    setOriginalFormValues(null);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.imei.trim()) {
      newErrors.imei = 'IMEI is required.';
    } else if (!/^\d{15}$/.test(form.imei)) {
      newErrors.imei = 'IMEI must be exactly 15 digits.';
    } else {
      const imeiDuplicate = devices.find(
        d => d.id !== editingId && d.imei === form.imei.trim()
      );
      if (imeiDuplicate) newErrors.imei = 'IMEI already exists.';
    }

    if (form.slNumber.trim()) {
      const slDuplicate = devices.find(
        d => d.id !== editingId && d.slNumber.toLowerCase() === form.slNumber.trim().toLowerCase()
      );
      if (slDuplicate) newErrors.slNumber = 'SL Number already exists.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ===== NEW FUNCTION: Handle status change with validation =====
  const handleStatusChange = (newStatus) => {
    // Check if trying to set to discarded
    if (newStatus === 'discarded') {
      // Confirm with user
      const confirm = window.confirm(
        `⚠️ Setting this device to "discarded" will make it permanently unavailable for use.\n\n` +
        `This action cannot be undone. Are you sure you want to continue?`
      );
      if (!confirm) return;
    }

    // Update form status
    setForm({ 
      ...form, 
      status: newStatus
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // Prepare data - allow companyId to be null if not selected
    const data = {
      imei: form.imei,
      slNumber: form.slNumber || '',
      model: form.model || '',
      make: form.make || '',
      purchaseDate: form.purchaseDate || '',
      status: form.status || 'new stock',
      image: form.image || '',
      // If company is selected, use it; otherwise set to null
      companyId: form.companyId || form.parentCompanyId || null,
      // For backward compatibility
      parentCompanyId: form.parentCompanyId || null
    };

    // If status is discarded, clear installation data
    if (form.status === 'discarded') {
      data.installationDate = null;
      data.installationWarrantyEnd = null;
    }

    if (editingId) {
      // Check if anything was actually changed
      if (!hasChanges(originalFormValues, form)) {
        setErrors({});
        resetForm();
        toast.info('No changes were made to the device.');
        return;
      }
      
      updateDevice(editingId, data);
      toast.success(`✅ Device with IMEI "${form.imei}" has been updated successfully!`);
    } else {
      addDevice(data);
      toast.success(`✅ Device with IMEI "${form.imei}" has been added successfully!`);
    }
    resetForm();
  };

  const handleEdit = (device) => {
    const selectedSubCompany = subCompanies.find(
      s => String(s.id) === String(device.companyId)
    );

    const formValues = {
      imei: device.imei,
      slNumber: device.slNumber || '',
      model: device.model || '',
      make: device.make || '',
      purchaseDate: device.purchaseDate || '',
      status: device.status || 'new stock',
      parentCompanyId: selectedSubCompany ? selectedSubCompany.parentCompanyId : (device.companyId || ''),
      companyId: selectedSubCompany ? device.companyId : '',
      image: device.image || ''
    };
    
    // Store original values for comparison
    setOriginalFormValues({
      imei: device.imei,
      slNumber: device.slNumber || '',
      model: device.model || '',
      make: device.make || '',
      purchaseDate: device.purchaseDate || '',
      status: device.status || 'new stock',
      parentCompanyId: selectedSubCompany ? selectedSubCompany.parentCompanyId : (device.companyId || ''),
      companyId: selectedSubCompany ? device.companyId : '',
      image: device.image || ''
    });
    
    setForm(formValues);
    setEditingId(device.id);
    setShowForm(true);
    setErrors({});
    setShowAddModelInput(false);
    setNewModel('');
  };

  const handleDelete = (deviceId) => {
    const deviceToDelete = devices.find(d => d.id === deviceId);
    if (deviceToDelete && window.confirm(`Are you sure you want to delete device with IMEI "${deviceToDelete.imei}"?`)) {
      deleteDevice(deviceId);
      toast.success(`Device with IMEI "${deviceToDelete.imei}" has been deleted successfully!`);
    }
  };

  // FIXED: Updated validateRow function with better error handling
  const validateRow = (row, index, allRows) => {
    const errs = [];
    const imei = (row.imei || '').toString().trim();
    const sl = (row.slNumber || '').toString().trim();

    // Validate IMEI
    if (!imei) {
      errs.push({ column: 'IMEI', message: 'Required' });
    } else if (!/^\d{15}$/.test(imei)) {
      errs.push({ column: 'IMEI', message: `Must be exactly 15 digits (current: ${imei.length} digits)` });
    } else {
      // Check for duplicates in database
      if (devices.some(d => d.imei === imei)) {
        errs.push({ column: 'IMEI', message: 'IMEI already exists in database' });
      }
      // Check for duplicates within the file
      const earlierImei = allRows.slice(0, index).some(r => (r.imei || '').toString().trim() === imei);
      if (earlierImei) {
        errs.push({ column: 'IMEI', message: 'Duplicate IMEI within this file' });
      }
    }

    // Validate SL Number if provided
    if (sl) {
      if (devices.some(d => d.slNumber && d.slNumber.toLowerCase() === sl.toLowerCase())) {
        errs.push({ column: 'SL Number', message: 'SL Number already exists in database' });
      }
      const earlierSl = allRows.slice(0, index).some(
        r => (r.slNumber || '').toString().trim().toLowerCase() === sl.toLowerCase()
      );
      if (earlierSl) {
        errs.push({ column: 'SL Number', message: 'Duplicate SL Number within this file' });
      }
    }

    // Validate company name if provided
    const companyName = (row.companyName || '').toString().trim();
    if (companyName) {
      const companyExists = companies.some(c => c.name.toLowerCase() === companyName.toLowerCase());
      if (!companyExists) {
        errs.push({ column: 'Company Name', message: `Company "${companyName}" not found in database` });
      }
    }

    // Validate sub-company name if provided
    const subCompanyName = (row.subCompanyName || '').toString().trim();
    if (subCompanyName && companyName) {
      const parentCompany = companies.find(c => c.name.toLowerCase() === companyName.toLowerCase());
      if (parentCompany) {
        const subExists = subCompanies.some(s => 
          String(s.parentCompanyId) === String(parentCompany.id) && 
          s.name.toLowerCase() === subCompanyName.toLowerCase()
        );
        if (!subExists) {
          errs.push({ column: 'Sub-Company Name', message: `Sub-company "${subCompanyName}" not found under "${companyName}"` });
        }
      }
    }

    // Validate status if provided
    const status = (row.status || '').toString().trim().toLowerCase();
    if (status && !['new stock', 'available', 'in use', 'discarded'].includes(status)) {
      errs.push({ column: 'Status', message: `Invalid status "${status}". Must be: new stock, available, in use, discarded` });
    }

    return errs;
  };

  // FIXED: Enhanced bulk add handler with better debugging
  const handleBulkAdd = (rows) => {
    console.log('=== BULK ADD STARTED ===');
    console.log('Rows to add:', rows);
    console.log('Current devices count:', devices.length);
    console.log('Available companies:', companies);
    console.log('Available sub-companies:', subCompanies);

    let addedCount = 0;
    let skippedCount = 0;
    const errors = [];
    const successRows = [];

    rows.forEach((row, index) => {
      try {
        console.log(`\n--- Processing Row ${index + 2} ---`);
        console.log('Row data:', row);

        const imei = (row.imei || '').toString().trim();
        if (!imei) {
          skippedCount++;
          errors.push(`Row ${index + 2}: IMEI is required`);
          console.log(`❌ Row ${index + 2}: IMEI is required`);
          return;
        }

        console.log(`IMEI: ${imei}`);

        // Check if IMEI already exists
        if (devices.some(d => d.imei === imei)) {
          skippedCount++;
          errors.push(`Row ${index + 2}: IMEI "${imei}" already exists`);
          console.log(`❌ Row ${index + 2}: IMEI already exists`);
          return;
        }

        // Find company
        let finalCompanyId = null;
        let parentCompanyId = null;
        
        const companyName = (row.companyName || '').toString().trim();
        console.log(`Company Name: "${companyName}"`);

        if (companyName) {
          const parentCompany = companies.find(c => c.name.toLowerCase() === companyName.toLowerCase());
          if (!parentCompany) {
            skippedCount++;
            errors.push(`Row ${index + 2}: Company "${companyName}" not found`);
            console.log(`❌ Row ${index + 2}: Company not found`);
            return;
          }
          console.log(`✓ Found parent company: ${parentCompany.name} (ID: ${parentCompany.id})`);
          
          parentCompanyId = parentCompany.id;
          finalCompanyId = parentCompany.id;

          // Check for sub-company
          const subCompanyName = (row.subCompanyName || '').toString().trim();
          console.log(`Sub-Company Name: "${subCompanyName}"`);
          
          if (subCompanyName) {
            const sub = subCompanies.find(s => 
              String(s.parentCompanyId) === String(parentCompany.id) && 
              s.name.toLowerCase() === subCompanyName.toLowerCase()
            );
            if (sub) {
              finalCompanyId = sub.id;
              console.log(`✓ Found sub-company: ${sub.name} (ID: ${sub.id})`);
            } else {
              skippedCount++;
              errors.push(`Row ${index + 2}: Sub-company "${subCompanyName}" not found under "${companyName}"`);
              console.log(`❌ Row ${index + 2}: Sub-company not found`);
              return;
            }
          }
        }

        // Prepare device data
        const deviceData = {
          imei: imei,
          slNumber: (row.slNumber || '').toString().trim(),
          model: (row.model || '').toString().trim(),
          make: (row.make || '').toString().trim(),
          purchaseDate: (row.purchaseDate || '').toString().trim(),
          status: (row.status || 'new stock').toString().toLowerCase().trim(),
          parentCompanyId: parentCompanyId,
          companyId: finalCompanyId,
          image: ''
        };

        console.log('Device data to add:', deviceData);

        // Add the device
        addDevice(deviceData);
        addedCount++;
        successRows.push(imei);
        console.log(`✅ SUCCESS: Added device with IMEI: ${imei}`);
        console.log(`   New device count would be: ${devices.length + 1}`);
      } catch (error) {
        skippedCount++;
        errors.push(`Row ${index + 2}: ${error.message}`);
        console.error(`❌ ERROR on row ${index + 2}:`, error);
        console.error('Error stack:', error.stack);
      }
    });

    // Show results
    console.log('\n=== BULK ADD COMPLETED ===');
    console.log(`Added: ${addedCount} devices`);
    console.log(`Skipped: ${skippedCount} rows`);
    console.log('Success rows:', successRows);
    console.log('Errors:', errors);

    if (addedCount > 0) {
      toast.success(`✅ ${addedCount} device(s) added successfully!`);
      // Force refresh by updating the page
      setTimeout(() => {
        window.location.reload();
      }, 30000);
    }
    
    if (skippedCount > 0) {
      toast.warning(`⚠️ ${skippedCount} row(s) skipped. Check console for details.`);
      console.error('Bulk upload errors:', errors);
      // Show first 5 errors in console
      errors.slice(0, 5).forEach((err, i) => {
        console.log(`  ${i + 1}. ${err}`);
      });
    }

    if (addedCount === 0 && skippedCount === 0) {
      toast.warning('No devices were added.');
    }

    setShowBulkAddMenu(false);
  };

  // Fixed bulk update handler
  const handleBulkUpdate = (rows) => {
    let updatedCount = 0;
    let skippedCount = 0;
    const errors = [];

    rows.forEach((row, index) => {
      try {
        const imei = (row.imei || '').toString().trim();
        if (!imei) {
          skippedCount++;
          errors.push(`Row ${index + 2}: IMEI is required for update`);
          return;
        }

        const device = devices.find(d => d.imei === imei);
        if (!device) {
          skippedCount++;
          errors.push(`Row ${index + 2}: Device with IMEI "${imei}" not found`);
          return;
        }

        const updated = { ...device };
        let hasChanges = false;

        // Update fields if provided
        if (row.installationDate) {
          updated.installationDate = row.installationDate;
          hasChanges = true;
        }

        if (row.installaionWarrantyend) {
          updated.installationWarrantyEnd = row.installaionWarrantyend;
          hasChanges = true;
        }

        if (row.status) {
          const newStatus = row.status.toString().toLowerCase().trim();
          updated.status = newStatus;
          hasChanges = true;
          
          // If status is discarded, clear installation data
          if (newStatus === 'discarded') {
            updated.installationDate = null;
            updated.installationWarrantyEnd = null;
          }
        }

        // Update company if provided
        const companyName = (row.companyName || '').toString().trim();
        if (companyName) {
          const parentCompany = companies.find(c => c.name.toLowerCase() === companyName.toLowerCase());
          if (parentCompany) {
            let finalCompanyId = parentCompany.id;
            const subCompanyName = (row.subCompanyName || '').toString().trim();
            if (subCompanyName) {
              const sub = subCompanies.find(s => 
                String(s.parentCompanyId) === String(parentCompany.id) && 
                s.name.toLowerCase() === subCompanyName.toLowerCase()
              );
              if (sub) {
                finalCompanyId = sub.id;
              }
            }
            updated.parentCompanyId = parentCompany.id;
            updated.companyId = finalCompanyId;
            hasChanges = true;
          }
        }

        if (hasChanges) {
          updateDevice(device.id, updated);
          updatedCount++;
        } else {
          skippedCount++;
          errors.push(`Row ${index + 2}: No changes provided for IMEI "${imei}"`);
        }
      } catch (error) {
        skippedCount++;
        errors.push(`Row ${index + 2}: ${error.message}`);
        console.error(`Error on row ${index + 2}:`, error);
      }
    });

    // Show results
    if (updatedCount > 0) {
      toast.success(`✅ ${updatedCount} device(s) updated successfully!`);
    }
    
    if (skippedCount > 0) {
      toast.warning(`⚠️ ${skippedCount} row(s) skipped. Check console for details.`);
      console.error('Bulk update errors:', errors);
    }

    if (updatedCount === 0 && skippedCount === 0) {
      toast.warning('No devices were updated.');
    }

    setShowBulkUpdateMenu(false);
  };

  // Handle adding new model
  const handleAddModel = () => {
    const trimmedModel = newModel.trim();
    if (!trimmedModel) {
      toast.warning('Please enter a model name.');
      return;
    }
    
    // Check if model already exists (case-insensitive)
    if (modelOptions.some(option => option.toLowerCase() === trimmedModel.toLowerCase())) {
      toast.warning('This model already exists in the list.');
      return;
    }
    
    // Add new model to options
    const updatedModels = [...modelOptions, trimmedModel];
    setModelOptions(updatedModels);
    
    // Save to localStorage
    localStorage.setItem('deviceModels', JSON.stringify(updatedModels));
    
    // Set the form model to the new model
    setForm({ ...form, model: trimmedModel });
    
    // Reset add model state
    setNewModel('');
    setShowAddModelInput(false);
    toast.success(`✅ Model "${trimmedModel}" has been added successfully!`);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Device Inventory</h1>
        {isSuperAdmin && (
          <div className="flex items-center space-x-3">
            <button onClick={() => { resetForm(); setShowForm(true); }} className="btn-primary">
              <FaPlus /> Add Device
            </button>

            {/* ====== BULK ADD OPTION WRAPPER ====== */}
            <div className="relative inline-block text-left">
              <button 
                onClick={() => {
                  setShowBulkAddMenu(!showBulkAddMenu);
                  setShowBulkUpdateMenu(false);
                }} 
                className="btn-secondary"
              >
                Bulk Add
              </button>
              
              {showBulkAddMenu && (
                <div className="absolute right-0 mt-2 p-2 bg-white rounded-xl shadow-lg border border-gray-100 z-50 flex flex-col gap-2 min-w-[200px]">
                  <BulkUpload
                    title="Device"
                    downloadLabel="Download Template"
                    uploadLabel="Bulk Add"
                    downloadData={devices.map(d => {
                      const subCompany = subCompanies.find(s => String(s.id) === String(d.companyId));
                      const parentCompany = subCompany
                        ? companies.find(c => String(c.id) === String(subCompany.parentCompanyId))
                        : companies.find(c => String(c.id) === String(d.companyId));

                      return {
                        imei: d.imei,
                        slNumber: d.slNumber || '',
                        model: d.model || '',
                        make: d.make || '',
                        purchaseDate: d.purchaseDate || '',
                        status: d.status || 'new stock',
                        companyName: parentCompany?.name || '',
                        subCompanyName: subCompany?.name || ''
                      };
                    })}
                    columns={[
                      { key: 'imei', label: 'IMEI' },
                      { key: 'slNumber', label: 'SL Number' },
                      { key: 'model', label: 'Device Model' },
                      { key: 'make', label: 'Device Make' },
                      { key: 'purchaseDate', label: 'Purchase Date (YYYY-MM-DD)' },
                      { key: 'status', label: 'Status' },
                      { key: 'companyName', label: 'Company Name' },
                      { key: 'subCompanyName', label: 'Sub-Company Name' }
                    ]}
                    sampleRow={{
                      imei: '123456789012345',
                      slNumber: 'SL001',
                      model: 'AIS 140',
                      make: 'ABC',
                      purchaseDate: '2025-06-15',
                      status: 'new stock',
                      companyName: 'Example Co',
                      subCompanyName: 'Sub1'
                    }}
                    onUpload={handleBulkAdd}
                    validateRow={validateRow}
                  />
                </div>
              )}
            </div>

            {/* ====== BULK UPDATE OPTION WRAPPER ====== */}
            <div className="relative inline-block text-left">
              <button 
                onClick={() => {
                  setShowBulkUpdateMenu(!showBulkUpdateMenu);
                  setShowBulkAddMenu(false);
                }} 
                className="btn-secondary"
              >
                Bulk Update
              </button>

              {showBulkUpdateMenu && (
                <div className="absolute right-0 mt-2 p-2 bg-white rounded-xl shadow-lg border border-gray-100 z-50 flex flex-col gap-2 min-w-[200px]">
                  <BulkUpload
                    title="Device Update"
                    downloadLabel="Download Template"
                    uploadLabel="Bulk Update"
                    downloadData={devices.map(d => ({
                      imei: d.imei,
                      slNumber: d.slNumber || '',
                      installationDate: d.installationDate || '',
                      status: d.status || '',
                      companyName: '',
                      subCompanyName: ''
                    }))}
                    columns={[
                      { key: 'imei', label: 'IMEI (required, reference only)' },
                      { key: 'slNumber', label: 'SL Number (reference only)' },
                      { key: 'installationDate', label: 'Installation Date (optional, YYYY-MM-DD)' },
                      { key: 'installaionWarrantyend', label: 'Installation Warranty End' },
                      { key: 'status', label: 'Status (optional)' },
                      { key: 'companyName', label: 'Company Name (optional)' },
                      { key: 'subCompanyName', label: 'Sub-Company Name (optional)' }
                    ]}
                    sampleRow={{
                      imei: '123456789012345',
                      slNumber: 'SL001',
                      installationDate: '2026-06-01',
                      status: 'available',
                      companyName: 'Example Co',
                      subCompanyName: 'Sub1'
                    }}
                    onUpload={handleBulkUpdate}
                    validateRow={() => []} // No validation for update
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ✅ RESPONSIVE Filters, Search & Download */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full lg:w-auto">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="flex-1 sm:flex-none rounded-xl border border-gray-300 px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[120px]"
            >
              <option value="">All Statuses</option>
              {uniqueStatuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Make:</label>
            <select
              value={makeFilter}
              onChange={(e) => { setMakeFilter(e.target.value); setCurrentPage(1); }}
              className="flex-1 sm:flex-none rounded-xl border border-gray-300 px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[120px]"
            >
              <option value="">All Makes</option>
              {uniqueMakes.map(make => (
                <option key={make} value={make}>{make}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Model:</label>
            <select
              value={modelFilter}
              onChange={(e) => { setModelFilter(e.target.value); setCurrentPage(1); }}
              className="flex-1 sm:flex-none rounded-xl border border-gray-300 px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[120px]"
            >
              <option value="">All Models</option>
              {uniqueModels.map(model => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-2 flex-1">
          <input
            type="text"
            placeholder="Search by IMEI or SL number…"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            className="w-full sm:flex-1 rounded-xl border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          
          <button 
            onClick={handleDownloadExcel}
            className="btn-secondary flex items-center gap-2 hover:bg-gray-100 whitespace-nowrap w-full sm:w-auto justify-center"
          >
            <FaDownload /> Download
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">IMEI</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">SL No</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Model</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Make</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Purchase Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Warranty End</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Installation Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Installation Warranty End</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Company</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {paginatedDevices.map(d => (
                <tr key={d.id} className="hover:bg-indigo-50/50 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{d.imei}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{d.slNumber || '-'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{d.model || '-'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{d.make || '-'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{formatDate(d.purchaseDate)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{formatDate(d.warrantyEnd)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{formatDate(d.installationDate)}</td>
                  
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {d.installationWarrantyEnd || d.installaionWarrantyend ? formatDate(d.installationWarrantyEnd || d.installaionWarrantyend) : '-'}
                  </td>

                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      String(d.status || '').toLowerCase().trim() === 'new stock' ? 'bg-yellow-100 text-yellow-800' :
                      String(d.status || '').toLowerCase().trim() === 'available' ? 'bg-green-100 text-green-800' :
                      String(d.status || '').toLowerCase().trim() === 'in use' ? 'bg-blue-100 text-blue-800' : 
                      String(d.status || '').toLowerCase().trim() === 'discarded' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {d.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {getEntityDisplayName(d.companyId) || '-'}
                  </td>
                  
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    {isSuperAdmin && (
                      <div className="flex space-x-2">
                        <button onClick={() => handleEdit(d)} className="text-indigo-600 hover:text-indigo-900 transition">
                          <FaEdit />
                        </button>
                        <button onClick={() => handleDelete(d.id)} className="text-red-500 hover:text-red-700 transition">
                          <FaTrash />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {paginatedDevices.length === 0 && (
                <tr>
                  <td colSpan="11" className="px-4 py-8 text-center text-sm text-gray-500">
                    No devices found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        totalItems={totalItems}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
      />

      {showForm && isSuperAdmin && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-2xl w-full max-w-lg mx-auto my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">{editingId ? 'Edit Device' : 'Add Device'}</h2>
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">IMEI Number (15 digits) *</label>
                <input
                  className="w-full rounded-xl border border-gray-300 px-3 sm:px-4 py-2 sm:py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={form.imei}
                  onChange={e => setForm({...form, imei: e.target.value.replace(/\D/g, '')})}
                  maxLength={15}
                />
                {errors.imei && <p className="text-red-500 text-xs mt-1">{errors.imei}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SL Number</label>
                <input
                  className="w-full rounded-xl border border-gray-300 px-3 sm:px-4 py-2 sm:py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={form.slNumber}
                  onChange={e => setForm({...form, slNumber: e.target.value})}
                />
                {errors.slNumber && <p className="text-red-500 text-xs mt-1">{errors.slNumber}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Device Model</label>
                <div className="flex items-center gap-2">
                  <select
                    className="flex-1 rounded-xl border border-gray-300 px-3 sm:px-4 py-2 sm:py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={form.model}
                    onChange={e => setForm({...form, model: e.target.value})}
                  >
                    <option value="">Select Model</option>
                    {modelOptions.map((model) => (
                      <option key={model} value={model}>{model}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowAddModelInput(!showAddModelInput)}
                    className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition flex items-center gap-1 text-sm whitespace-nowrap"
                    title="Add New Model"
                  >
                    <FaPlusCircle size={14} /> Add
                  </button>
                </div>
                {showAddModelInput && (
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Enter new model name..."
                      value={newModel}
                      onChange={(e) => setNewModel(e.target.value)}
                      className="flex-1 rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddModel();
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleAddModel}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-medium transition"
                    >
                      Add Model
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddModelInput(false);
                        setNewModel('');
                      }}
                      className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-xl text-sm font-medium transition"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Device Make</label>
                <select
                  className="w-full rounded-xl border border-gray-300 px-3 sm:px-4 py-2 sm:py-2.5 focus:outline-none"
                  value={form.make}
                  onChange={e => setForm({...form, make: e.target.value})}
                >
                  <option value="">Select Make</option>
                  <option value="Atlanta"> Atlanta </option>
                  <option value="Teltanica"> Teltanica </option>
                  <option value="Itriangle"> Itriangle </option>
                  <option value="Bharat 101"> Bharat 101 </option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Date</label>
                <input
                  type="date"
                  className="w-full rounded-xl border border-gray-300 px-3 sm:px-4 py-2 sm:py-2.5 focus:outline-none"
                  value={form.purchaseDate}
                  onChange={e => setForm({...form, purchaseDate: e.target.value})}
                />
              </div>
              
              {/* ===== UPDATED STATUS SELECT WITH HANDLER ===== */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  className="w-full rounded-xl border border-gray-300 px-3 sm:px-4 py-2 sm:py-2.5 bg-white"
                  value={form.status}
                  onChange={e => handleStatusChange(e.target.value)}
                >
                  <option value="new stock">New Stock</option>
                  <option value="available">Available</option>
                  <option value="in use">In Use</option>
                  <option value="discarded">Discarded</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company (Optional)</label>
                <select
                  className="w-full rounded-xl border border-gray-300 px-3 sm:px-4 py-2 sm:py-2.5 bg-white"
                  value={form.parentCompanyId}
                  onChange={(e) => setForm({ ...form, parentCompanyId: e.target.value, companyId: '' })}
                >
                  <option value="">-- No Company --</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>
              {form.parentCompanyId && getSubCompaniesByCompany(form.parentCompanyId).length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sub Company</label>
                  <select
                    className="w-full rounded-xl border border-gray-300 px-3 sm:px-4 py-2 sm:py-2.5 bg-white"
                    value={form.companyId}
                    onChange={(e) => setForm({ ...form, companyId: e.target.value })}
                  >
                    <option value="">Select Sub Company</option>
                    {getSubCompaniesByCompany(form.parentCompanyId).map((sub) => (
                      <option key={sub.id} value={sub.id}>{sub.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={resetForm} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">{editingId ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceInventory;